import { Clock, Logger } from "@highmarch/app";
import { recordDiag } from "./diagLog";

export const systemClock: Clock = { utcNowSeconds: () => Math.floor(Date.now() / 1000) };

/**
 * The game's Logger: the console, and the diagnostics ring buffer (Settings → Copy diagnostics). Warnings and errors
 * reach the buffer through its console hook (diagLog.ts), info lines here.
 */
export const consoleLogger: Logger = {
  log(level, message) {
    if (level === "info") recordDiag(level, message);
    if (level === "error") console.error(message);
    else if (level === "warning") console.warn(message);
    else console.log(message);
  },
};

/**
 * Browser capabilities the save-keeping and return features lean on, each feature-detected (a missing API is a
 * quiet no-op): WebKit clears a Safari tab's storage after 7 days of browsing without a visit, a home-screen app
 * keeps it, and persist() asks the browser not to evict it under storage pressure.
 */
export const webDevice = {
  /** Asks once per boot for persistent storage; resolves whether the browser says the site's storage is persistent. */
  async persistStorage(): Promise<boolean> {
    try {
      const s = navigator.storage;
      if (!s?.persist) return false;
      if (await s.persisted?.()) return true;
      return await s.persist();
    } catch { return false; }
  },
  /** Running as a home-screen app (standalone display), where the save is safe from the 7-day eviction. */
  isStandalone(): boolean {
    try {
      return (navigator as Navigator & { standalone?: boolean }).standalone === true || matchMedia("(display-mode: standalone)").matches;
    } catch { return false; }
  },
  /** iPhone / iPad Safari (iPadOS reports a Mac with touch): the install card shows Safari's share-sheet steps. */
  isAppleMobile(): boolean {
    return /iPhone|iPad|iPod/.test(navigator.userAgent) || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
  },
  /** The home-screen icon's number (0 clears it); only home-screen apps show one. */
  setAppBadge(n: number): void {
    const nav = navigator as Navigator & { setAppBadge?: (n?: number) => Promise<void>; clearAppBadge?: () => Promise<void> };
    try {
      if (n > 0) void nav.setAppBadge?.(n)?.catch(() => undefined);
      else void nav.clearAppBadge?.()?.catch(() => undefined);
    } catch { /* unsupported */ }
  },
  /** Hands text to the share sheet, else the clipboard; resolves how it went ("failed" leaves the caller to show it). */
  async shareText(title: string, text: string): Promise<"shared" | "copied" | "failed"> {
    try {
      if (navigator.share) { await navigator.share({ title, text }); return "shared"; }
    } catch (err) {
      if ((err as Error)?.name === "AbortError") return "failed";
    }
    try { await navigator.clipboard.writeText(text); return "copied"; } catch { return "failed"; }
  },
};

/** Small per-device numbers (timestamps for once-a-week cards, when the page was last seen): never game state. */
export const deviceMarks = {
  get(key: string): number | null {
    try { const v = globalThis.localStorage?.getItem(`highmarch.mark.${key}`); return v === null || v === undefined ? null : Number(v); } catch { return null; }
  },
  set(key: string, value: number): void {
    try { globalThis.localStorage?.setItem(`highmarch.mark.${key}`, String(value)); } catch { /* private mode */ }
  },
};

/** Chromium's install prompt, held from the moment the browser offers it (Safari never does: the card shows steps). */
interface InstallPromptEvent extends Event { prompt(): Promise<void>; userChoice: Promise<{ outcome: "accepted" | "dismissed" }> }
let installPrompt: InstallPromptEvent | null = null;
if (typeof window !== "undefined") {
  window.addEventListener("beforeinstallprompt", (e) => { e.preventDefault(); installPrompt = e as InstallPromptEvent; });
}

/** The browser's own install dialog, when it has offered one; resolves whether the player accepted. */
export function takeInstallPrompt(): (() => Promise<boolean>) | null {
  const p = installPrompt;
  if (!p) return null;
  return async () => {
    installPrompt = null;
    try { await p.prompt(); return (await p.userChoice).outcome === "accepted"; } catch { return false; }
  };
}
